import * as CryptoJS from "crypto-js";
import { CURRENT_BUILD_VERSION, E_K1, E_K2, LOGROCKET_APP_ID, MINIMUM_BUILD_VERSION } from "@/constants/allconstants";
import { ALL_FEATURES, ENVELOPE_STATUS, MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, customDisposableDomains } from "@/utils/constants/constants";
import { errorMessages } from "@/utils/constants/messages";
import disposableEmailDomains from 'disposable-email-domains';
import { removeOrganizationData, setAllOrganizations, setCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import LogRocket from 'logrocket'
import { getFeaturesListFromAPI } from "@/services/setting.service";
import { getToken } from "@/utils/functions/TokenFunction";

export const updateBuildVersion = (): void => {
    const Local_build = localStorage.getItem("build_version");

    if (!Local_build) {
        localStorage.clear();
        if (CURRENT_BUILD_VERSION) {
            localStorage.setItem("build_version", String(CURRENT_BUILD_VERSION));
        }
        return;
    }
    
    if (Local_build === "development") {
        return;
    }
    
    const parsedBuildVersion = parseInt(Local_build, 10);
    
    if (MINIMUM_BUILD_VERSION) {
        const min_ver = parseInt(String(MINIMUM_BUILD_VERSION), 10);
        if (min_ver > parsedBuildVersion) {
            localStorage.clear();
            if (CURRENT_BUILD_VERSION) {
                localStorage.setItem("build_version", String(CURRENT_BUILD_VERSION));
            }
            return;
        }
    }
}
export const storeInLocalStorage = (key: string, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
};

export const setDataInLocalStorage = (key: string, value: any) => {
    localStorage.setItem(key, value);
};

export const getFromLocalStorage = (key: string) => {
    updateBuildVersion()
    return localStorage.getItem(key);
};

export const removeFromLocalStorage = (key: string) => {
    localStorage.removeItem(key);
};
export const clearLocalStorage = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('name')
    localStorage.removeItem('email_address')
    localStorage.removeItem('userId')
    localStorage.removeItem('all_features')
    removeOrganizationData()
    removeDirectoryConfigrationsFromLocalStorage()
    clearLoginSessionSecrets()
};

export const clearLoginSessionSecrets = () => {
    sessionStorage.removeItem('admin_login_password');
    localStorage.removeItem('otp_session_token');
};

export const storeLoginPasswordForOtp = (password: string) => {
    sessionStorage.setItem('admin_login_password', password);
};

export const isExternalLogoutRedirect = (redirect: string) => {
    try {
        const url = new URL(redirect, window.location.origin);
        // Compare host (hostname:port), not origin — http vs https on the same
        // admin host should stay in-app so we don't flash the HTML boot loader.
        return url.host !== window.location.host;
    } catch {
        return false;
    }
};

export const resolveLogoutPath = (redirect?: string | null) => {
    if (!redirect) {
        return '/';
    }

    try {
        const url = new URL(redirect, window.location.origin);
        if (url.host === window.location.host) {
            return `${url.pathname}${url.search}${url.hash}` || '/';
        }
    } catch {
        return '/';
    }

    return '/';
};

export const getEncrypted = (message: any) => {
    if (!message) {
        return;
    }
    return CryptoJS.AES.encrypt(message, `${E_K1}${E_K2}`).toString();
};

export const getDecrypted = (cipherText: any) => {
    if (!cipherText) {
        return;
    }
    const bytes = CryptoJS.AES.decrypt(cipherText, `${E_K1}${E_K2}`);
    return bytes.toString(CryptoJS.enc.Utf8);
};

export const checkPassword = (newPass: string) => {
    if (newPass === "" || newPass === null || newPass === undefined) {
        return errorMessages.PASSWORD_REQUIRED;
    }
    if (MIN_PASSWORD_LENGTH > newPass.length || newPass.length > MAX_PASSWORD_LENGTH) {
        return errorMessages.PASSWORD_LENGTH_ERROR;
    }
    if (newPass.match(/\d/g) === null) {
        return errorMessages.PASSWORD_NUMBER_ERROR;
    }
    if (newPass.match(/[a-z]/g) === null) {
        return errorMessages.PASSWORD_LOWER_CASE_ERROR;
    }
    if (newPass.match(/[A-Z]/g) === null) {
        return errorMessages.PASSWORD_UPPER_CASE_ERROR;
    }
    if (newPass.match(/[-._!"`'#%&,:;<>=@{}~$()*+/\\?[\]^|]+/) === null) {
        return errorMessages.PASSWORD_SYMBOL_ERROR;
    }
};

export function isEmail(emailAdress: string) {
    if (!emailAdress) {
        return false;
    }
    let regex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*\.\w{2,25}$/;
    return !!emailAdress.match(regex);
}

export const getInitials = (str: string) => {
    return str
        ?.match(/(\b\S)?/g)
        ?.join("")
        .match(/(^\S|\S$)?/g)
        ?.join("")
        .toUpperCase();
};

export const DateFormatter = (dateString: string) => {
    if(!dateString) return;
    const date = new Date(dateString);
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    const day = date.getUTCDate().toString().padStart(2, "0");
    const year = date.getUTCFullYear().toString();
    
    return `${month}/${day}/${year}`;
};

export const TimeFormatter = (time: string) => {
    if(!time) return;
    return new Date(time)
        .toLocaleString()
        .replace(/:\d{3}\s/, " ")
        .split(", ")[1]
        ?.toLowerCase();
};

export const convertToSentenceCase = (text: string) => {
    if (typeof text !== "string" || text.length === 0) {
        return "";
    }

    const sentences = text.toLowerCase().split(". ");

    const sentenceCaseText = sentences.map((sentence) => {
        const trimmedSentence = sentence.trim();
        if (trimmedSentence.length > 0) {
            const firstChar = trimmedSentence.charAt(0).toUpperCase();
            const restOfSentence = trimmedSentence.slice(1);
            return firstChar + restOfSentence;
        }
        return "";
    });

    return sentenceCaseText.join(". ");
};
export function getStatusClass(status: string) {
    switch (status) {
        case ENVELOPE_STATUS.COMPLETED:
            return "badge-outline-success";
        case ENVELOPE_STATUS.DECLINED:
        case ENVELOPE_STATUS.EXPIRED:
            return "badge-outline-danger";
        case ENVELOPE_STATUS.DRAFT:
            return "badge-outline-secondary";
        case ENVELOPE_STATUS.CORRECTING:
            return "badge-outline-warning";
        case ENVELOPE_STATUS.VOIDED:
                return "badge-outline-secondary";
        default:
            return "badge-outline-success";
    }
}

export const convertBase64 = (file: Blob) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
            resolve(fileReader.result);
        };
        fileReader.onerror = (error) => {
            reject(error);
        };
    });
};

export const validateEmail = (email: string) => {
    return String(email).toLowerCase().match(/^[a-zA-Z0-9]+(?:[._+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/)
};

export function isDisposableEmail(email: string) {
    const [, domain] = email.split('@');
    return disposableEmailDomains.includes(domain) || customDisposableDomains.includes(domain);
  }

export const storeLoginUserData = (response: any) => {
    try {
        if (response.access_token) {
            storeInLocalStorage("token", getEncrypted(response.access_token));
            clearLoginSessionSecrets();
            void getFeaturesListFromAPI()
                .then((featuresList) => {
                    storeInLocalStorage("all_features", featuresList.data.features);
                    window.dispatchEvent(new Event("gs-features-loaded"));
                })
                .catch((err) => console.log(err));
        }
        setDataInLocalStorage("name", getEncrypted(response.full_name || concatenateName(response.first_name, response.last_name)));
        setDataInLocalStorage("email_address", getEncrypted(response.email_address));
        setDataInLocalStorage("userId", getEncrypted(response.user_id || response.id));

        if (LOGROCKET_APP_ID) {
            LogRocket.identify(response.user_id || response.id, {
                name: response.full_name || concatenateName(response.first_name, response.last_name),
                email: response.email_address,
            });
        }
        const organizations = response.organizations;
        setAllOrganizations(organizations);
        const curr_organization = response.current_organization || response.organizations[0]
        setCurrentOrganization(curr_organization);
        removeFromLocalStorage("session_id");
        void setFeaturesListInLocalStorage();
    } catch (err) {
        console.log(err)
    }
}

export const checkSSOLoginAllowed = () => {
    const passwordEnabledString = localStorage.getItem('sso_login_enabled');
    
    // Ensure passwordEnabledString is not null or undefined
    if (passwordEnabledString === null || passwordEnabledString === undefined) {
        return false;
    }
    
    // Parse the value to a boolean
    try {
        return JSON.parse(passwordEnabledString);
    } catch (error) {
        console.error("Error parsing JSON from localStorage:", error);
        return false;
    }
};

export const checkSSOLoginRestricted = () => {
    const passwordEnabledString = localStorage.getItem('sso_login_restricted');
    
    // Ensure passwordEnabledString is not null or undefined
    if (passwordEnabledString === null || passwordEnabledString === undefined) {
        return false;
    }
    
    // Parse the value to a boolean
    try {
        return JSON.parse(passwordEnabledString);
    } catch (error) {
        console.error("Error parsing JSON from localStorage:", error);
        return false;
    }
};

export function getBaseUrl() {
    const url = window.location.href;
    const queryStringIndex = url.indexOf('?');
    return queryStringIndex === -1 ? url : url.split('?')[0];
}
export const removeDirectoryConfigrationsFromLocalStorage = () => {
    localStorage.removeItem('loginWithSSO')
    localStorage.removeItem('organization_id')
    localStorage.removeItem('sso_login_restricted')
    localStorage.removeItem('sso_login_enabled')
    localStorage.removeItem('switch_org_id')
}
export const setFeaturesListInLocalStorage = async () => {
    if (!getToken()) {
        return;
    }

    try {
        const featuresList = await getFeaturesListFromAPI();
        if (featuresList?.data?.features) {
            storeInLocalStorage(ALL_FEATURES, featuresList.data.features);
        }
    } catch (error) {
        console.log(error);
    }
};
export const concatenateName = (firstName: string, lastName: string) => {
    if (!firstName && !lastName) return "";
    if (!firstName) return lastName;
    if (!lastName) return firstName;
    return `${firstName} ${lastName}`;
}

export type PersonLike = {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email_address?: string | null;
} | null | undefined;

export const getPersonDisplayName = (person: PersonLike) => {
    if (!person) {
        return "";
    }
    if (person.full_name?.trim()) {
        return person.full_name.trim();
    }
    return concatenateName(person.first_name ?? "", person.last_name ?? "");
};

export const formatPersonWithEmail = (person: PersonLike) => {
    const name = getPersonDisplayName(person);
    const email = person?.email_address;
    if (name && email) {
        return `${name} (${email})`;
    }
    return name || email || "";
};

export const getMaskedApiKey = (key: string) => {
    if (!key) return '';
    const firstFour = key.substring(0, 4);
    const lastFour = key.substring(key.length - 4);
    return `${firstFour}${'*'.repeat(8)}${lastFour}`;
}